import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';

import {
  ORGANIZATIONS,
  ORGANIZATION_MEMBER_FIRST_NAMES,
  ORGANIZATION_MEMBER_LAST_NAMES,
  ORGANIZATION_MEMBER_ROLES,
  ORGANIZATION_PAYMENT_METHODS,
  PROPERTIES,
} from '@common/data';
import type {
  Organization,
  OrganizationBilling,
  OrganizationMember,
  OrganizationMemberDraft,
  OrganizationMemberPage,
  OrganizationMemberStatus,
  OrganizationPage,
  OrganizationQuery,
  Property,
} from '@common/types';

import { UserPreferencesService } from './user-preferences.service';

@Injectable({ providedIn: 'root' })
export class OrganizationService {
  private readonly prefs = inject(UserPreferencesService);

  readonly organizations: ReadonlyArray<Organization> = ORGANIZATIONS;

  private readonly state = this.prefs.organizations;

  readonly hasMultiple = this.organizations.length > 1;

  readonly primaryId = computed<string | null>(
    () => this.resolveId(this.state().primaryId) ?? this.organizations[0]?.id ?? null,
  );

  readonly activeId = computed<string | null>(
    () => this.resolveId(this.state().activeId) ?? this.primaryId(),
  );

  readonly primary = computed<Organization | null>(() => this.byId(this.primaryId()));
  readonly active = computed<Organization | null>(() => this.byId(this.activeId()));

  private readonly _members = signal<Record<string, OrganizationMember[]>>(
    Object.fromEntries(this.organizations.map((org) => [org.id, this.buildMembers(org.id)])),
  );

  private memberSeq = 0;

  byId(id: string | null): Organization | null {
    if (!id) return null;
    return this.organizations.find((org) => org.id === id) ?? null;
  }

  isActive(id: string): boolean {
    return this.activeId() === id;
  }

  isPrimary(id: string): boolean {
    return this.primaryId() === id;
  }

  select(id: string): void {
    if (!this.byId(id)) return;
    this.prefs.patch('organizations', { activeId: id });
  }

  setPrimary(id: string): void {
    if (!this.byId(id)) return;
    this.prefs.patch('organizations', { primaryId: id });
  }

  list(query: OrganizationQuery): Observable<OrganizationPage> {
    let result: Organization[] = this.organizations.slice();

    const term = query.search.trim().toLowerCase();
    if (term.length > 0) {
      result = result.filter(
        (org) =>
          org.name.toLowerCase().includes(term) ||
          org.location.toLowerCase().includes(term),
      );
    }

    if (query.sortField) {
      const field = query.sortField as keyof Organization;
      const direction = query.sortOrder === 'desc' ? -1 : 1;
      result.sort((a, b) => {
        const av = a[field];
        const bv = b[field];
        if (av < bv) return -1 * direction;
        if (av > bv) return 1 * direction;
        return 0;
      });
    }

    const total = result.length;
    const start = Math.max(0, query.first);
    const end = start + Math.max(1, query.rows);
    const data = result.slice(start, end);

    return of({ data, total });
  }

  members(orgId: string): OrganizationMember[] {
    return this._members()[orgId] ?? [];
  }

  inviteMember(orgId: string, draft: OrganizationMemberDraft): OrganizationMember | null {
    const org = this.byId(orgId);
    if (!org) return null;
    this.memberSeq += 1;
    const name = draft.name.trim();
    const member: OrganizationMember = {
      id: `${orgId}-mbr-invite-${this.memberSeq}`,
      name,
      initials: this.initials(name),
      email: draft.email.trim().toLowerCase(),
      role: draft.role,
      status: 'invited',
      accountCount: 0,
      joinedAtIso: new Date().toISOString(),
    };
    this.updateMembers(orgId, (members) => [...members, member]);
    return member;
  }

  removeMember(orgId: string, memberId: string): void {
    this.updateMembers(orgId, (members) => members.filter((member) => member.id !== memberId));
  }

  setMemberStatus(orgId: string, memberId: string, status: OrganizationMemberStatus): void {
    this.updateMembers(orgId, (members) =>
      members.map((member) => (member.id === memberId ? { ...member, status } : member)),
    );
  }

  private updateMembers(
    orgId: string,
    change: (members: OrganizationMember[]) => OrganizationMember[],
  ): void {
    this._members.update((all) => ({ ...all, [orgId]: change(all[orgId] ?? []) }));
  }

  private buildMembers(orgId: string): OrganizationMember[] {
    const org = this.byId(orgId);
    if (!org) return [];
    const domain = this.emailDomain(org.name);
    const base = this.seed(orgId);
    const joined = new Date(org.joinedAtIso).getTime();
    const firsts = ORGANIZATION_MEMBER_FIRST_NAMES;
    const lasts = ORGANIZATION_MEMBER_LAST_NAMES;
    const usedEmails = new Set<string>();
    const members: OrganizationMember[] = [];
    for (let i = 0; i < org.memberCount; i += 1) {
      const first = firsts[(base + i * 7) % firsts.length];
      const last = lasts[(base + i * 13 + 3) % lasts.length];
      const name = `${first} ${last}`;
      const handle = `${first}.${last}`.toLowerCase();
      let email = `${handle}@${domain}`;
      let suffix = 2;
      while (usedEmails.has(email)) {
        email = `${handle}${suffix}@${domain}`;
        suffix += 1;
      }
      usedEmails.add(email);
      const role =
        i === 0
          ? 'Owner'
          : ORGANIZATION_MEMBER_ROLES[(base + i) % ORGANIZATION_MEMBER_ROLES.length];
      members.push({
        id: `${orgId}-mbr-${i + 1}`,
        name,
        initials: this.initials(name),
        email,
        role,
        status: this.memberStatus(base + i),
        accountCount: this.memberAccountCount(role, base + i, org.accountCount),
        joinedAtIso: new Date(joined + ((base + i * 29) % 900) * 86_400_000).toISOString(),
      });
    }
    return members;
  }

  private memberAccountCount(role: string, n: number, accountCount: number): number {
    if (accountCount <= 0) return 0;
    if (role === 'Owner' || role === 'Admin') return accountCount;
    return 1 + (n % Math.max(1, accountCount));
  }

  accountsFor(orgId: string): ReadonlyArray<Property> {
    const org = this.byId(orgId);
    if (!org) return [];
    return PROPERTIES.slice(0, org.accountCount);
  }

  billing(orgId: string): OrganizationBilling | null {
    const org = this.byId(orgId);
    if (!org) return null;
    const base = this.seed(orgId);
    return {
      currency: 'USD',
      billingEmail: `billing@${this.emailDomain(org.name)}`,
      renewalIso: this.nextRenewal(org.joinedAtIso),
      status: 'active',
      paymentMethod:
        ORGANIZATION_PAYMENT_METHODS[base % ORGANIZATION_PAYMENT_METHODS.length],
    };
  }

  listMembers(orgId: string, query: OrganizationQuery): Observable<OrganizationMemberPage> {
    let result = this.members(orgId);

    const term = query.search.trim().toLowerCase();
    if (term.length > 0) {
      result = result.filter(
        (member) =>
          member.name.toLowerCase().includes(term) ||
          member.email.toLowerCase().includes(term) ||
          member.role.toLowerCase().includes(term),
      );
    }

    const statusFilters = query.filters['status'];
    if (statusFilters?.length) {
      result = result.filter((member) => statusFilters.includes(member.status));
    }

    if (query.sortField) {
      const field = query.sortField as keyof OrganizationMember;
      result = this.sortBy(result, field, query.sortOrder);
    }

    return of(this.pageOf(result, query));
  }

  private sortBy<T>(rows: T[], field: keyof T, order: 'asc' | 'desc' | null): T[] {
    const direction = order === 'desc' ? -1 : 1;
    return rows.slice().sort((a, b) => {
      const av = a[field];
      const bv = b[field];
      if (av < bv) return -1 * direction;
      if (av > bv) return 1 * direction;
      return 0;
    });
  }

  private pageOf<T>(rows: T[], query: OrganizationQuery): { data: T[]; total: number } {
    const total = rows.length;
    const start = Math.max(0, query.first);
    const end = start + Math.max(1, query.rows);
    return { data: rows.slice(start, end), total };
  }

  private memberStatus(n: number): OrganizationMemberStatus {
    if (n % 11 === 4) return 'invited';
    if (n % 17 === 9) return 'suspended';
    return 'active';
  }

  private nextRenewal(joinedAtIso: string): string {
    const now = new Date();
    const joined = new Date(joinedAtIso);
    const renewal = new Date(now.getFullYear(), joined.getMonth(), joined.getDate());
    if (renewal.getTime() <= now.getTime()) renewal.setFullYear(now.getFullYear() + 1);
    return renewal.toISOString();
  }

  private emailDomain(name: string): string {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '');
    return `${slug || 'organisation'}.com`;
  }

  private initials(name: string): string {
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  private seed(id: string): number {
    let hash = 0;
    for (let i = 0; i < id.length; i += 1) {
      hash = (hash * 31 + id.charCodeAt(i)) % 100_000;
    }
    return hash;
  }

  private resolveId(id: string | null): string | null {
    return id && this.byId(id) ? id : null;
  }
}
